import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, Phone, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: 'Philosophy', id: 'about', testId: 'nav-link-about' },
  { label: 'Sales Engine', id: 'services', testId: 'nav-link-services' },
  { label: 'Developers', id: 'partners', testId: 'nav-link-partners' },
  { label: 'Direct Inquiry', id: 'contact', testId: 'nav-link-contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goHome = () => {
    setIsOpen(false);
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goSection = (id) => {
    setIsOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 120);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-3 z-50 px-3 sm:top-5 sm:px-6"
      data-testid="site-header"
    >
      <div className={`mx-auto flex max-w-7xl items-center justify-between rounded-2xl border px-3 py-3 transition-all duration-500 sm:px-4 ${isScrolled ? 'glass-panel border-[#0B1F3B]/15 shadow-[0_24px_70px_rgba(6,16,30,0.14)]' : 'border-white/40 bg-[#F5F5F5]/70 backdrop-blur-xl'}`}>
        <button onClick={goHome} className="group flex items-center gap-3 text-left" data-testid="header-brand-logo">
          <img src="https://customer-assets.emergentagent.com/job_plotted-homes-india/artifacts/e6z3ucbf_Next%20Leap%20-%20Print.png" alt="Next Leap Inc" className="h-11 w-auto object-contain" />
          <span className="hidden border-l border-[#0B1F3B]/15 pl-3 sm:block">
            <span className="block font-display text-base font-semibold leading-none tracking-tight">Next Leap Inc</span>
            <span className="mt-1 block font-tech text-[10px] uppercase tracking-[0.22em] text-[#0B1F3B]/55">Real estate mandates</span>
          </span>
        </button>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
          <Link to="/projects" data-testid="nav-link-projects" className={`rounded-full px-4 py-2 font-tech text-[11px] uppercase tracking-[0.18em] transition-colors ${location.pathname.startsWith('/projects') ? 'bg-[#0B1F3B] text-[#F5F5F5]' : 'text-[#0B1F3B]/70 hover:bg-[#0B1F3B]/10 hover:text-[#0B1F3B]'}`}>Mandates</Link>
          {navItems.map((item) => (
            <button key={item.id} data-testid={item.testId} onClick={() => goSection(item.id)} className="rounded-full px-4 py-2 font-tech text-[11px] uppercase tracking-[0.18em] text-[#0B1F3B]/70 transition-colors hover:bg-[#0B1F3B]/10 hover:text-[#0B1F3B]">
              {item.label}
            </button>
          ))}
          <Link to="/admin" data-testid="nav-link-admin" className={`rounded-full px-4 py-2 font-tech text-[11px] uppercase tracking-[0.18em] transition-colors ${location.pathname.startsWith('/admin') ? 'bg-[#C5A880] text-[#0B1F3B]' : 'text-[#0B1F3B]/70 hover:bg-[#0B1F3B]/10 hover:text-[#0B1F3B]'}`}>Operations</Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Button asChild className="h-11 rounded-full border border-[#0B1F3B]/15 bg-white/70 px-5 font-tech text-[11px] uppercase tracking-[0.16em] text-[#0B1F3B] shadow-none hover:bg-white">
            <a href="tel:+917039310297" data-testid="header-phone-link"><Phone className="h-4 w-4" />70393 10297</a>
          </Button>
          <Button onClick={() => goSection('contact')} data-testid="header-consultation-btn" className="h-11 rounded-full bg-[#0B1F3B] px-5 font-tech text-[11px] uppercase tracking-[0.16em] text-[#F5F5F5] transition-transform hover:-translate-y-0.5 hover:bg-[#06101E]">
            Private consult
          </Button>
        </div>

        <button onClick={() => setIsOpen(!isOpen)} className="rounded-full border border-[#0B1F3B]/15 p-3 lg:hidden" aria-label="Toggle navigation menu" data-testid="mobile-menu-toggle">
          {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.28 }} className="mx-auto mt-3 max-w-7xl rounded-3xl border border-[#0B1F3B]/10 bg-[#F5F5F5]/95 p-4 shadow-2xl backdrop-blur-xl lg:hidden" data-testid="mobile-navigation-panel">
            <div className="grid gap-2">
              <Link to="/projects" onClick={() => setIsOpen(false)} className="rounded-2xl bg-white px-4 py-4 font-display text-2xl">Mandates</Link>
              {navItems.map((item) => <button key={item.id} onClick={() => goSection(item.id)} className="rounded-2xl bg-white px-4 py-4 text-left font-display text-2xl">{item.label}</button>)}
              <Link to="/admin" onClick={() => setIsOpen(false)} className="rounded-2xl bg-[#0B1F3B] px-4 py-4 font-display text-2xl text-[#F5F5F5]">Operations</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
